import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from 'lodash';
import cx from 'classnames';

import { AssetCard, PlaceholderAssetCard } from "../card";
import DraggableGrid, { DraggableGridHandle } from 'ruuri';

import { AssetLayoutProps } from "../../../types";
import Button from "../../../../../components/button";

import styles from './grid.module.scss';
import { USER_DEFINED } from "src/constants/collections";
import { ContactCard } from "../contactCard";
import { CollectionAsset } from "src/types/collections";

interface HoverState {
  section: number;
  asset: string;
  position: {
    left: number;
    top: number;
  };
}

const GridLayout: React.FC<AssetLayoutProps> = ({ loading, assets, menuState, onMenuChange, onClick, onRemove, onMove, onMoveToSection, collection, section}) => {
  const gridRef = React.useRef<DraggableGridHandle | null>(null);
  const assetsRef = useRef(assets);

  const editing = useRef(menuState.primaryAction === 'edit');
  const showPlaceholder = loading && _.size(assets) === 0;
  const empty = !loading && _.size(assets) === 0;
  
  const canEdit = useMemo(() => {
      return collection?.type == USER_DEFINED && collection?.canEdit;
    }, [collection]);

  const [hoverOverSection, setHoverOverSection] = useState<HoverState | null>(null);

  const [sortedAssets, setSortedAssets] = React.useState<CollectionAsset[]>([]);
  const [assetsOrder, setAssetsOrder] = React.useState<string[]>([]);
  
  useEffect(() => {
    editing.current = menuState.primaryAction === 'edit';

    const assetsSortedByOrder = _.sortBy(assets, ['order']);
    setSortedAssets(assetsSortedByOrder);
    const order = assetsSortedByOrder.map(item => item.id);
    setAssetsOrder(order);
  }, [menuState.primaryAction, assets]);

  useEffect(() => {
    const grid = gridRef.current?.grid;
    if (!grid) return;

    const handleAdd = () => {
      const order = sortedAssets.map(a => a.id);

      grid.sort((a, b) => {
        const idA = a.getElement()?.dataset.ruuriId;
        const idB = b.getElement()?.dataset.ruuriId;

        return order.indexOf(idA!) - order.indexOf(idB!);
      });

      grid.layout(true);
    };

    grid.on('add', handleAdd);

    return () => {
      grid.off('add', handleAdd);
    };
  }, [sortedAssets]);

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);


  useEffect(() => {
    synchronizeGridState();
  }, [assets]);

  const synchronizeGridState = () => {
    const gridItems = gridRef.current?.grid?.getItems() || [];
    const missingItems = gridItems.filter(item => {
      const itemId = item.getElement()?.getAttribute('data-ruuri-id');

      return !assets.find(asset => asset.id == itemId);
    });

    if (_.size(missingItems) > 0) {
      gridRef.current?.grid?.remove(missingItems);
    }

    //gridRef.current?.grid?.add(existingItems);
    gridRef.current?.grid?.synchronize();
    gridRef.current?.grid?.layout(true);
  }

  const handleDimensionsChange = _.debounce(() => {
    gridRef.current?.grid?.refreshItems();
    gridRef.current?.grid?.layout(true);
  }, 300);

  const handleAssetRemove = (asset) => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  const onDragStart = useCallback(() => {
    return editing.current;
  }, [editing.current]);

  const onDragMove = (item, event) => {
    const element = item.getElement();
    if (!element) {
      return;
    }

    const { section, over } = findSectionIndex(event);
    const asset = item.getElement()?.getAttribute('data-ruuri-id');
    const rect = element.getBoundingClientRect();

    if (section >= 0) {
      setHoverOverSection({
        section: section,
        asset: asset,
        position: {
          left: event.clientX - rect.left,
          top: event.clientY - rect.top
        }
      });
    } else if (over) {
      setHoverOverSection({
        section: -1,
        asset: asset,
        position: {
          left: event.clientX - rect.left,
          top: event.clientY - rect.top
        }
      });
    } else {
      setHoverOverSection(null);
    }
  };

  const onDragEnd = (item, event) => {
    const element = item.getElement();
    if (!element) {
      return;
    }
    const { section } = findSectionIndex(event);
    const asset = item.getElement()?.getAttribute('data-ruuri-id');

    if(section >= 0 && asset) {
      const assetItem = assetsRef.current.find(item => item.id == asset);
      if(assetItem){
        onMoveToSection(section, assetItem);
      }
    }

    setHoverOverSection(null);
    gridRef.current?.grid?.layout(true);
  }

  const findSectionNode = (elements: Element[]): Element | null => {
    for (const element of elements) {
      if (element.classList.contains('collection_section')) {
        return element;
      }
  
      let parent = element.parentElement;
  
      while (parent) {
        if (parent.classList.contains('collection_section')) {
          return parent;
        }
  
        parent = parent.parentElement;
      }
    }
  
    return null;
  };

  const findSectionIndex = (event) => {
    const elements = document.elementsFromPoint(event.clientX, event.clientY);

    if (elements == null) {
      return {
        section: -1,
        over: false
      };
    }

    const sectionNode = findSectionNode(elements);

    const sectionBar = elements.find(element => {
      if (element.classList.contains('collection_sections')) {
        return element;
      }

      return null;
    });

    if (sectionNode) {
      const index = sectionNode.getAttribute('data-section-index');
      return {
        section: index ? parseInt(index) : -1,
        over: sectionBar != null
      }
    }

    return {
      section: -1,
      over: sectionBar != null
    };
  }

  return (
    <div className={styles.grid}>
      {showPlaceholder && (
        <div className={styles.placeholders}>
          <PlaceholderAssetCard height={400} />
          <PlaceholderAssetCard height={200} />
          <PlaceholderAssetCard height={500} />
          <PlaceholderAssetCard height={250} />
        </div>
      )}

      {empty && <EmptyAssets canEdit={canEdit || false} onMenuChange={onMenuChange} />}

      {!showPlaceholder && (
        <DraggableGrid
          ref={gridRef}
          data={sortedAssets}
          uniKey={"id"}
          itemClass={styles.item}
          itemDraggingClass={styles.dragging}
          renderItem={(itemData) => {
            if (hoverOverSection && _.get(hoverOverSection, "asset") == _.get(itemData, "id")) {
              const section = hoverOverSection.section;

              return (
                <div
                  className={cx(styles.dragOverSection, {
                    [styles.disabled]: section < 0,
                  })}
                  style={hoverOverSection.position}
                >
                  Add to section
                </div>
              );
            }

            if (_.get(itemData, "asset.entityType") === "asset") {
              return (
                <AssetCard
                  asset={itemData}
                  editable={menuState.primaryAction === "edit"}
                  onDimensionsChange={handleDimensionsChange}
                  onRemove={handleAssetRemove}
                  onClick={onClick}
                  collection={collection}
                  sectionIndex={section}
                />
              );
            } else if (_.get(itemData, "type") === "CONTACT_CARD") {

            return (
              <ContactCard
                key={_.get(itemData, "id")}
                asset={itemData}
                editable={menuState.primaryAction === "edit"}
                onRemove={handleAssetRemove}
                />
              )
            }
          }}
          layout={{
            fillGaps: false,
            horizontal: false,
            alignRight: false,
            alignBottom: false,
            rounding: false,
          }}
          showDuration={0}
          sortData={null}
          dragEnabled={true}
          dragSort={true}
          dragStartPredicate={onDragStart}
          onMove={(data) => {
            const element = data.item.getElement();
            if (!element) {
              return;
            }

            onMove(data.fromIndex, data.toIndex);
          }}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
        />
      )}
    </div>
  );
}

interface EmptyAssetsProps {
  canEdit: boolean;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (state: any) => void;
}

const EmptyAssets: React.FC<EmptyAssetsProps> = ({ canEdit, onMenuChange }) => {
  const handleAddAsset = () => {
    onMenuChange({
      primaryAction: 'edit',
      secondaryAction: 'add-asset',
    });
  }

  return (
    <div className={styles.empty}>
      <div className={styles.card}>
        <img src="/images/collections/empty.svg" />
      </div>
      <div className={cx(styles.card, styles.message)}>
        <p>Time to build out your collection, asset by asset, section by section...</p>
        {canEdit && <Button variant={'primary'} icon="fa-solid fa-pencil" text="Edit" onClick={handleAddAsset} />}
      </div>
    </div>
  );
}

export { GridLayout };